import { FilterableTable } from 'components/tables/filterable-table';
import { formatISO } from 'date-fns';
import {
  fromApiResponses,
  UpgradeTemplatesTableElement,
} from 'pages/upgrade_templates/data-model';
import { UpgradeBadgeStatus } from 'pages/upgrade_templates/helpers';
import { useSplitPanel } from 'contexts/split-panel';
import { UpgradeTemplateDetails } from 'pages/upgrade_templates/details';
import CopyText from 'components/copy-text';
import { PropertyFilterToken } from '@cloudscape-design/collection-hooks';
import { UpgradeTemplateStatus } from 'api/admin-models';
import { useListUpgradeTemplatesInfinite } from 'api/admin-infinite';

export function AccountUpgradeTemplates(props: {
  orgId: string;
  accountId: string;
}): JSX.Element {
  const updateSplitPanel = useSplitPanel();
  const { data, mutate } = useListUpgradeTemplatesInfinite(props.accountId);
  const isGamma = import.meta.env.VITE_API_ENDPOINT.includes('chkk.dog');

  return (
    <FilterableTable<UpgradeTemplatesTableElement>
      selectionMode="single"
      data={fromApiResponses(data)}
      columns={[
        {
          id: 'Id',
          label: 'ID',
          cell: (item) => (
            <CopyText
              copyText={item.Id}
              copyButtonLabel="Click to copy"
              successText="Copied"
              errorText="Failed to copy"
            />
          ),

          defaultVisible: true,

          sortingField: 'Id',
          filterOperators: ['=', '!='],
        },
        {
          id: 'Name',
          label: 'Name',
          cell: (item) => item.Name || '-',

          defaultVisible: true,

          sortingField: 'Name',
          filterOperators: ['=', '!=', ':', '!:'],
        },
        {
          id: 'Environment',
          label: 'Environment',
          cell: (item) => item.Environment || '-',

          defaultVisible: !isGamma,

          sortingField: 'Environment',
          filterOperators: ['=', '!=', ':', '!:'],
        },
        {
          id: 'ResourceId',
          label: 'Resource ID',
          cell: (item) => (
            <CopyText
              copyText={item.ResourceId || '-'}
              copyButtonLabel="Click to copy"
              successText="Copied"
              errorText="Failed to copy"
            />
          ),

          defaultVisible: true,

          sortingField: 'ResourceId',
          filterOperators: ['=', '!='],
        },
        {
          id: 'Status',
          label: 'Status',
          cell: (item) => (
            <UpgradeBadgeStatus value={item.Status || 'pending'} />
          ),

          defaultVisible: true,

          sortingField: 'Status',
          filterOperators: ['=', '!='],
          filterOptions: [
            'pending',
            'in-progress',
            'available',
            'completed',
            'cancellation-in-progress',
            'cancelled',
            'outdated',
            'update-in-progress',
            'regenerate-in-progress',
            'approved',
          ],
        },
        {
          id: 'CreationStatus',
          label: 'Creation Status',
          cell: (item) => (
            <UpgradeBadgeStatus
              value={item.CreationStatus || 'pending-assignment'}
            />
          ),

          defaultVisible: true,

          sortingField: 'CreationStatus',
          filterOperators: ['=', '!='],
          filterOptions: [
            'pending-assignment',
            'in-progress',
            'in-review',
            'published',
          ],
        },
        {
          id: 'Assignee',
          label: 'Assignee',
          cell: (item) => item.Assignee || '-',
          sortingField: 'Assignee',

          defaultVisible: !isGamma,

          filterOperators: ['=', '!='],
        },
        {
          id: 'Creation',
          label: 'Created',
          cell: (item) => formatISO(new Date(item.Created)),

          defaultVisible: true,

          sortingField: 'Created',
        },
      ]}
      defaultSorting={{ field: 'Creation', direction: 'descending' }}
      defaultFilterQuery={(searchParams) => {
        const tokens: PropertyFilterToken[] = [];

        tokens.push({
          propertyKey: 'Status',
          value: UpgradeTemplateStatus.cancelled,
          operator: '!=',
        });
        return Promise.resolve({ tokens: tokens, operation: 'and' });
      }}
      selectionTrackBy={(item) => `${item.AccountId}#${item.Id}`}
      onSelectionChange={(event) => {
        updateSplitPanel(
          <UpgradeTemplateDetails
            key={`${event.detail.selectedItems[0].AccountId}:${event.detail.selectedItems[0].Id}`}
            upgrade={{
              orgId: props.orgId,
              accountId: event.detail.selectedItems[0].AccountId,
              upgradeId: event.detail.selectedItems[0].Id,
            }}
            onChange={() => void mutate()}
          />,
        );
      }}
    />
  );
}
